import {
  AbortMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand
} from "@aws-sdk/client-s3";
import {
  getUploadJob,
  updateUploadJob,
  updateUploadSource
} from "./uploadPersistence";
import { createProtectedS3Client, getProtectedStorageTarget } from "./s3Client";

export const DEFAULT_UPLOAD_PART_SIZE = 8 * 1024 * 1024;

export class UploadCancelledError extends Error {
  constructor() {
    super("Upload was cancelled.");
    this.name = "UploadCancelledError";
  }
}

function isAbortError(error) {
  return Boolean(
    error && (error.name === "AbortError" || error.code === "AbortError")
  );
}

function completedBytesForSource(source, partSize) {
  return (source.completedParts || []).reduce((total, part) => {
    if (Number.isFinite(part.size)) return total + part.size;
    const start = (part.PartNumber - 1) * partSize;
    return total + Math.max(0, Math.min(partSize, source.size - start));
  }, 0);
}

export function createS3MultipartUploadTask({
  db,
  job,
  sources,
  scheduler,
  client = createProtectedS3Client(),
  partSize = DEFAULT_UPLOAD_PART_SIZE
}) {
  if (!db || !job || !job.id || !Array.isArray(sources) || !sources.length) {
    throw new Error("A persisted upload job and its sources are required.");
  }
  if (!scheduler || typeof scheduler.run !== "function") {
    throw new Error("A shared upload scheduler is required.");
  }

  const target = getProtectedStorageTarget(job.accountId);
  const schedulerState =
    typeof scheduler.getState === "function" ? scheduler.getState() : {};
  const partBatchSize = Math.max(
    1,
    Number(schedulerState.maxConcurrency) || 4
  );
  const activeControllers = new Set();
  const inFlightRequests = new Set();
  let paused = false;
  let cancelled = false;
  let failedError = null;
  let pauseVersion = 0;
  let resumeWaiters = [];
  let persistenceChain = Promise.resolve();

  function waitUntilActive() {
    if (cancelled) return Promise.reject(new UploadCancelledError());
    if (failedError) return Promise.reject(failedError);
    if (!paused) return Promise.resolve();
    return new Promise((resolve, reject) => {
      resumeWaiters.push({ resolve, reject });
    });
  }

  function persistProgress(source) {
    const sourceSnapshot = {
      ...source,
      completedParts: [...(source.completedParts || [])],
      completedBytes: completedBytesForSource(source, partSize),
      updatedAt: new Date().toISOString()
    };
    persistenceChain = persistenceChain.then(async () => {
      await updateUploadSource({ db, source: sourceSnapshot });
      const record = await getUploadJob({ db, jobId: job.id });
      if (!record) return;
      const completedBytes = record.sources.reduce(
        (total, candidate) =>
          total + completedBytesForSource(candidate, partSize),
        0
      );
      await updateUploadJob({
        db,
        job: {
          ...record.job,
          completedBytes,
          updatedAt: new Date().toISOString()
        }
      });
    });
    return persistenceChain;
  }

  async function send(command) {
    const controller = new AbortController();
    activeControllers.add(controller);
    const request = client.send(command, { abortSignal: controller.signal });
    inFlightRequests.add(request);
    try {
      return await request;
    } finally {
      activeControllers.delete(controller);
      inFlightRequests.delete(request);
    }
  }

  async function sendScheduled(createCommand) {
    await waitUntilActive();
    const requestPauseVersion = pauseVersion;
    try {
      return await scheduler.run(async () => {
        await waitUntilActive();
        return send(createCommand());
      });
    } catch (error) {
      if (cancelled) throw new UploadCancelledError();
      if (
        isAbortError(error) &&
        (paused || requestPauseVersion !== pauseVersion)
      ) {
        return sendScheduled(createCommand);
      }
      throw error;
    }
  }

  async function ensureMultipartUpload(source) {
    if (source.multipartUploadId) return source.multipartUploadId;
    await waitUntilActive();
    const response = await sendScheduled(
      () =>
        new CreateMultipartUploadCommand({
          Bucket: target.bucket,
          Key: target.prefix + source.storageKey,
          ContentType: source.type || "application/octet-stream",
          Metadata: source.metadata || {}
        })
    );
    source.multipartUploadId = response.UploadId;
    source.status = "running";
    await updateUploadSource({
      db,
      source: { ...source, updatedAt: new Date().toISOString() }
    });
    return source.multipartUploadId;
  }

  async function uploadPart(source, partNumber) {
    await waitUntilActive();
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, source.size);
    const response = await sendScheduled(
      () =>
        new UploadPartCommand({
          Bucket: target.bucket,
          Key: target.prefix + source.storageKey,
          UploadId: source.multipartUploadId,
          PartNumber: partNumber,
          Body: source.blob.slice(start, end)
        })
    );
    source.completedParts = [
      ...(source.completedParts || []).filter(
        part => part.PartNumber !== partNumber
      ),
      { PartNumber: partNumber, ETag: response.ETag, size: end - start }
    ].sort((a, b) => a.PartNumber - b.PartNumber);
    await persistProgress(source);
  }

  async function uploadSource(source) {
    await ensureMultipartUpload(source);
    const partCount = Math.max(1, Math.ceil(source.size / partSize));

    while (source.completedParts.length < partCount) {
      await waitUntilActive();
      const completedPartNumbers = new Set(
        source.completedParts.map(part => part.PartNumber)
      );
      const pendingPartNumbers = Array.from(
        { length: partCount },
        (_, index) => index + 1
      )
        .filter(partNumber => !completedPartNumbers.has(partNumber))
        .slice(0, partBatchSize);
      await Promise.all(
        pendingPartNumbers.map(partNumber => uploadPart(source, partNumber))
      );
    }

    await waitUntilActive();
    await sendScheduled(
      () =>
        new CompleteMultipartUploadCommand({
          Bucket: target.bucket,
          Key: target.prefix + source.storageKey,
          UploadId: source.multipartUploadId,
          MultipartUpload: {
            Parts: source.completedParts.map(({ PartNumber, ETag }) => ({
              PartNumber,
              ETag
            }))
          }
        })
    );
    source.status = "complete";
    source.completedBytes = source.size;
    await updateUploadSource({
      db,
      source: { ...source, updatedAt: new Date().toISOString() }
    });
    return { key: source.storageKey, sourceId: source.id };
  }

  const workingSources = sources.map(sourceRecord => ({
    ...sourceRecord,
    completedParts: [...(sourceRecord.completedParts || [])]
  }));

  const result = (async () => {
    try {
      const results = await Promise.all(
        workingSources.map(source =>
          source.status === "complete"
            ? { key: source.storageKey, sourceId: source.id }
            : uploadSource(source)
        )
      );
      await persistenceChain;
      return { key: results[0].key, files: results };
    } catch (error) {
      if (!paused && !cancelled) {
        failedError = error;
        activeControllers.forEach(controller => controller.abort());
        await Promise.all(
          Array.from(inFlightRequests).map(request => request.catch(() => null))
        );
      }
      throw error;
    }
  })();

  return {
    result,
    pause() {
      if (!paused) pauseVersion += 1;
      paused = true;
      activeControllers.forEach(controller => controller.abort());
    },
    resume() {
      if (!paused || cancelled) return;
      paused = false;
      const waiters = resumeWaiters;
      resumeWaiters = [];
      waiters.forEach(waiter => waiter.resolve());
    },
    async cancel() {
      cancelled = true;
      paused = false;
      const waiters = resumeWaiters;
      resumeWaiters = [];
      waiters.forEach(waiter => waiter.reject(new UploadCancelledError()));
      activeControllers.forEach(controller => controller.abort());
      await Promise.all(
        Array.from(inFlightRequests).map(request => request.catch(() => null))
      );
      await Promise.all(
        workingSources
          .filter(
            source => source.multipartUploadId && source.status !== "complete"
          )
          .map(source =>
            client
              .send(
                new AbortMultipartUploadCommand({
                  Bucket: target.bucket,
                  Key: target.prefix + source.storageKey,
                  UploadId: source.multipartUploadId
                })
              )
              .catch(() => null)
          )
      );
    }
  };
}

export function createS3MultipartTaskFactory({ db, scheduler }) {
  return ({ job, sources }) =>
    createS3MultipartUploadTask({ db, job, sources, scheduler });
}
